import { default as EmberRouterService } from '@ember/routing/router-service';
import { service } from '@ember/service';
import type SessionContextService from '../session-context';
import type SessionService from '../session';
import type { RouteArgs } from '@ember/routing/lib/utils';
import type Transition from '@ember/routing/transition';

export default class RouterService extends EmberRouterService {
  @service declare sessionContext: SessionContextService;
  @service declare session: SessionService;

  transitionTo(...args: RouteArgs): Transition {
    // @ts-expect-error: Not sure how to add qp to the type
    const query = args[args.length - 1]?.queryParams;
    const hasQuery = typeof query === 'object';
    const emptyQuery = hasQuery && Object.keys(query).length === 0;
    const routeOrUrl = args.length > 0 || !hasQuery ? args[0] : '';

    if (!emptyQuery || !this.currentRoute || this.currentRoute.name !== routeOrUrl) {
      return super.transitionTo(...args);
    }

    // If a route transition was requested for the same page and an empty query
    // was explicitly passed in, treat it as if all query params were reset
    const queryParams = Object.keys(this.currentRoute.queryParams).reduce((params, key) => {
      // @ts-expect-error: Not sure how to add qp to the type
      params[key] = undefined;

      return params;
    }, {});

    return super.transitionTo(...[...args.slice(0, -1), { queryParams }]);
  }

  /**
   * Resolves once the app is no longer on `fromRouteName`.
   *
   * Post-authentication redirects cannot be awaited directly — ember-simple-auth
   * starts them inside `session.authenticate` — so a submit task awaits this to
   * stay running, and so keep its button busy, until the user actually lands.
   *
   * Pass the route being left, read *before* the operation that redirects. The
   * comparison is what makes this safe: a transition that already finished
   * resolves immediately, where waiting on the next `routeDidChange` would hang
   * forever having missed the event. Never rejects — an aborted transition in a
   * redirect chain simply leaves the route unchanged and keeps waiting.
   *
   * @param fromRouteName route the user is leaving, e.g. `currentRouteName`
   * @returns a promise that resolves once the current route differs
   */
  waitForRouteChangeFrom(fromRouteName: string | null | undefined): Promise<void> {
    if (this.currentRouteName !== fromRouteName) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const onRouteDidChange = () => {
        if (this.currentRouteName === fromRouteName) {
          return;
        }
        this.off('routeDidChange', onRouteDidChange);
        resolve();
      };
      this.on('routeDidChange', onRouteDidChange);
    });
  }

  /**
   * Checks if a redirect is present in the URL query string and persists it
   * via ESA so the post-authentication flow restores it. This is the
   * mechanism for explicit `?redirect=/foo` deep links (e.g. links emailed to
   * users that should land on a specific page after login).
   */
  detectRedirectParameter() {
    const redirect = this.currentRoute?.queryParams.redirect;

    if (typeof redirect === 'string' && redirect.length > 0) {
      this.session.setRedirectTarget(redirect);
    }
  }
}
